import React, { useEffect, useState } from "react";
import axios from "axios";
import AdminLayout, { adminHeaders, useAdminAuthed } from "@/components/AdminLayout";
import { Link } from "react-router-dom";
import { ShoppingCart, Users, Package, AlertTriangle, TrendingUp, IndianRupee, Boxes, Megaphone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rupees = (paise) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format((paise || 0) / 100);

const Stat = ({ icon: Icon, label, value, tone = "default", to }) => {
  const bg = tone === "danger" ? "bg-red-50" : tone === "warn" ? "bg-amber-50" : "bg-white/70";
  const border = tone === "danger" ? "border-red-100" : tone === "warn" ? "border-amber-100" : "border-[var(--hk-border)]";
  const inner = (
    <div className={`rounded-2xl border ${border} ${bg} p-5 hover:shadow-sm transition-shadow`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[var(--hk-ink)] text-[var(--hk-ivory)] flex items-center justify-center">
          <Icon size={18} />
        </div>
        <p className="text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">{label}</p>
      </div>
      <p className="hk-h2 mt-3 leading-none" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</p>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};

const AdminDashboardPage = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const pw = useAdminAuthed();
  useEffect(() => {
    if (!pw) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/summary`, { headers: adminHeaders() });
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load dashboard");
      }
    })();
  }, [pw]);

  return (
    <AdminLayout title="Dashboard" subtitle="Live snapshot of every corner of the store">
      {err && <p className="text-red-600 text-sm">{err}</p>}
      {!data && !err && <p className="text-[var(--hk-ink-2)]">Loading…</p>}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={IndianRupee} label="Total Sales (paid)" value={rupees(data.total_sales_paise)} />
            <Stat icon={ShoppingCart} label="Paid Orders" value={data.paid_orders} to="/admin/orders" />
            <Stat icon={TrendingUp} label="Average Order" value={rupees(data.aov_paise)} />
            <Stat icon={Users} label="Customers" value={data.customers} to="/admin/customers" />
            <Stat icon={Package} label="Products" value={data.products} to="/admin/products" />
            <Stat icon={Megaphone} label="Enquiries" value={data.enquiries} to="/admin/enquiries" />
            <Stat icon={AlertTriangle} label="Low Stock" value={data.low_stock} tone={data.low_stock ? "warn" : "default"} to="/admin/inventory" />
            <Stat icon={Boxes} label="Out of Stock" value={data.out_stock} tone={data.out_stock ? "danger" : "default"} to="/admin/inventory" />
          </div>

          <div className="mt-8 hk-card overflow-hidden">
            <div className="px-5 py-4 border-b border-[var(--hk-border)] flex items-center">
              <h3 className="hk-h4">Recent Orders</h3>
              <Link to="/admin/orders" className="ml-auto text-[13px] text-[var(--hk-gold-deep,#B85E24)] hover:underline">View all →</Link>
            </div>
            <table className="w-full text-[13px]">
              <thead className="bg-[var(--hk-warm-ivory)] text-[11px] uppercase tracking-widest text-[var(--hk-ink-2)]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Order</th>
                  <th className="text-left px-4 py-3 font-medium">Customer</th>
                  <th className="text-left px-4 py-3 font-medium">Amount</th>
                  <th className="text-left px-4 py-3 font-medium">Payment</th>
                  <th className="text-left px-4 py-3 font-medium">Placed</th>
                </tr>
              </thead>
              <tbody>
                {(data.recent_orders || []).map((o) => (
                  <tr key={o.id} className="border-t border-[var(--hk-border)]">
                    <td className="px-4 py-3 font-mono text-[12px]">{o.id.slice(0, 8)}</td>
                    <td className="px-4 py-3">{o.customer_name}</td>
                    <td className="px-4 py-3">{rupees(o.total)}</td>
                    <td className="px-4 py-3">
                      <span className={
                        "text-[11px] px-2 py-0.5 rounded-full border " +
                        (o.payment_status === "paid" ? "border-green-200 bg-green-50 text-green-800"
                         : o.payment_status === "failed" ? "border-red-200 bg-red-50 text-red-700"
                         : "border-amber-200 bg-amber-50 text-amber-700")
                      }>{o.payment_status}</span>
                    </td>
                    <td className="px-4 py-3 text-[var(--hk-ink-2)]">{new Date(o.created_at).toLocaleString("en-IN")}</td>
                  </tr>
                ))}
                {(!data.recent_orders || data.recent_orders.length === 0) && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-[var(--hk-ink-2)]">No orders yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminDashboardPage;
